import type { ComponentProps, FC, KeyboardEvent } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import {
    Dropdown,
    DropdownActionButton,
    FormField,
    Inline,
    Paragraph,
    TextInput,
} from '@george-labs.com/design-system';

import { Translation, t } from '@luna/i18n';
import { logger } from '@luna/logger';
import { ENTER_KEY_CODE, TAB_KEY_CODE } from '@luna/a11y';

import {
    getApiErrorMessage,
    getTranslatedAliasChangeError,
    validateAlias,
} from './alias-change/aliasChangeService';
import type { AliasChangeError, AliasChangeValidationConfig } from './alias-change/typing';

export type AliasChangeDropdownProps = {
    toggle: ComponentProps<typeof Dropdown>['toggle'];
    onAliasSave: (alias: string) => Promise<unknown>;
    validationConfig: AliasChangeValidationConfig;
    onAliasChangeSuccess?: () => void;
    onAliasChangeError?: (error: Error) => void;
    onClose?: () => void;
    onOpen?: () => void;
    alias?: string;
    hasAlias?: boolean;
    isOpen?: boolean;
    placeholder?: string;
    maxInputLength?: number;
};

export const AliasChangeDropdown: FC<AliasChangeDropdownProps> = ({
    toggle,
    alias = '',
    onAliasSave,
    onAliasChangeSuccess,
    onAliasChangeError,
    validationConfig,
    hasAlias = true,
    isOpen,
    onClose,
    onOpen,
    placeholder,
    maxInputLength,
}) => {
    const [productAlias, setProductAlias] = useState(alias);
    const [[validationError = '', validationErrorDataCy = ''], setValidationError] =
        useState<AliasChangeError>([]);
    const [[apiError = '', apiErrorDataCy = ''], setApiError] = useState<AliasChangeError>([]);
    const toggleRef = useRef<HTMLElement | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const saveButtonRef = useRef<HTMLButtonElement>(null);

    const isSaveDisabled = alias === productAlias || Boolean(validationError);

    const focusAliasInput = () => {
        requestAnimationFrame(() => {
            inputRef.current?.focus({ preventScroll: true });
        });
    };

    const focusSaveButton = () => {
        requestAnimationFrame(() => {
            saveButtonRef.current?.focus();
        });
    };

    const handleAliasSave = () => {
        if (isSaveDisabled) {
            return;
        }

        onAliasSave(productAlias)
            .then(() => {
                setApiError([]);
                onAliasChangeSuccess?.();
            })
            .catch((error) => {
                logger.error(error);
                onAliasChangeError?.(error?.responseJSON?.errors);
                setApiError(getApiErrorMessage(error?.responseJSON?.errors));
            });
    };

    const handleClose = () => {
        toggleRef.current?.focus();
        onClose?.();
    };

    const inputValue = hasAlias ? alias : undefined;

    const handleOnButtonClick = () => {
        // @ts-expect-error GDS workaround - Button onClick is overridden by Dropdown onClick
        toggle?.props?.onClick?.();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        const { keyCode } = event;
        if (keyCode === ENTER_KEY_CODE) {
            handleOnButtonClick();
        }
    };

    const handleAliasInputKeyDown = (event: KeyboardEvent) => {
        const { keyCode, shiftKey } = event;
        if (keyCode === ENTER_KEY_CODE) {
            event.preventDefault();
            event.stopPropagation();
            handleAliasSave();
            return;
        }

        if (keyCode === TAB_KEY_CODE && !shiftKey && !isSaveDisabled) {
            event.preventDefault();
            focusSaveButton();
        }
    };

    useLayoutEffect(() => {
        if (isOpen) {
            focusAliasInput();
        }
    }, [isOpen]);

    const handleOpen = () => {
        onOpen?.();
        focusAliasInput();
    };

    return (
        <Dropdown
            alignment={Dropdown.ALIGNMENT.LEFT}
            toggle={toggle}
            toggleRef={toggleRef}
            isOpen={isOpen}
            onClose={handleClose}
            onOpen={handleOpen}
            onClick={handleOnButtonClick}
            onKeyDown={handleKeyDown}
            closeOnScroll={false}
            actionButton={
                <DropdownActionButton
                    data-cy="dropdown-alias-change-button"
                    innerRef={saveButtonRef}
                    disabled={isSaveDisabled}
                    onClick={handleAliasSave}
                    closeOnClick={false}
                >
                    <Translation t="app.save" />
                </DropdownActionButton>
            }
        >
            <FormField
                required
                label={<Inline data-cy="dropdown-alias-change-label">{t('app.name')}</Inline>}
                error={
                    (validationError || apiError) && (
                        <Paragraph data-cy={validationErrorDataCy || apiErrorDataCy} role="alert">
                            {getTranslatedAliasChangeError(validationError || apiError)}
                        </Paragraph>
                    )
                }
                labelColumnWidth={12}
                input={
                    <TextInput
                        type={TextInput.TYPE.TEXT}
                        id="aliasInput"
                        name="aliasNameInput"
                        value={inputValue}
                        placeholder={placeholder}
                        maxLength={maxInputLength}
                        onChange={(event) => {
                            setValidationError(validateAlias(event.target.value, validationConfig));
                            setProductAlias(event.target.value);
                        }}
                        onKeyDown={handleAliasInputKeyDown}
                        data-cy="dropdown-alias-change-input"
                        innerRef={inputRef}
                    />
                }
            />
        </Dropdown>
    );
};
